import * as React from "react";
import { useSelector } from "react-redux";
import { useState } from "react";
import { Alert, Button, Dialog } from "@mui/material";
import DataTable, {
    customValueRenderer,
    dateFilterColumn,
    freeTextColumn,
    multiSelectColumn,
} from "./DataTable";
import { CA_EVENT_TYPE, INDEX_ACTION_TYPE } from "../modules/actionTypes";

/**
 * Future actions - a security's CAs/IAs dated after it stops trading (delisting CA / disabled from date).
 * The backend asks for confirmation before saving such a change: it returns the `future_actions` (without saving),
 * and the request is then resent with `trash_future_actions` = true (move them to trash - their date is set to
 * 01/01/2500) / false (keep them as they are). With true, the response lists the `trashed_actions`.
 */

/**
 * Displays future/trashed actions (see above) - corporate actions and index actions in separate tables.
 *
 * @param actions list of actions as returned by the API, each of: { _id, kind ("corporate_action" / "index_action"),
 *      date, event_type (CAs) / action_type (IAs), index_id, security_id, ric, name,
 *      delisting_execution_date / disabled_from }
 */
export function FutureActionsTables({ actions }) {
    const indexesById = useSelector((state) => state.indexes.indexesById);

    const indexColumn = multiSelectColumn(
        "index_id",
        "Index",
        customValueRenderer((x) => (x ? indexesById[x]?.internal_id : "")),
    );
    // Why the actions are affected - only one of these is set, depending on the flow (delisting / disabling)
    const reasonColumns = [
        ["delisting_execution_date", "Delisting Execution Date"],
        ["disabled_from", "Disabled From"],
    ]
        .filter(([name]) => actions.some((a) => a[name]))
        .map(([name, label]) => dateFilterColumn(name, label));

    const sections = [
        {
            title: "Corporate Actions",
            rows: actions.filter((a) => a.kind === "corporate_action"),
            columns: [
                freeTextColumn("ric", "RIC"),
                freeTextColumn("name", "Name"),
                multiSelectColumn(
                    "event_type",
                    "CA Type",
                    customValueRenderer((x) => CA_EVENT_TYPE[x]),
                ),
                dateFilterColumn("date", "Execution Date"),
                indexColumn,
                ...reasonColumns,
            ],
        },
        {
            title: "Index Actions",
            rows: actions.filter((a) => a.kind === "index_action"),
            columns: [
                freeTextColumn("ric", "RIC"),
                freeTextColumn("name", "Name"),
                indexColumn,
                multiSelectColumn(
                    "action_type",
                    "Action Type",
                    customValueRenderer((x) => INDEX_ACTION_TYPE[x]),
                ),
                dateFilterColumn("date", "Effective Date"),
                ...reasonColumns,
            ],
        },
    ];

    return (
        <div className="flex flex-col gap-6 max-w-full">
            {sections
                .filter((s) => s.rows.length > 0)
                .map((s) => (
                    <div key={s.title}>
                        <div className="font-bold mb-4">
                            {s.title} ({s.rows.length}):
                        </div>
                        <div className="overflow-y-hidden">
                            <DataTable
                                title={s.title}
                                data={s.rows}
                                columns={s.columns}
                            />
                        </div>
                    </div>
                ))}
        </div>
    );
}

/**
 * Hook that handles the future actions confirmation of an API call (see above).
 *
 * Returns:
 *  - submitWithConfirmation: (async function) receives `submit` - an async function that calls the API with the
 *      given extra body params (merged into the request body) and returns the response. Calls it, and if the
 *      response has future actions, asks the user what to do with them and calls it again with
 *      `trash_future_actions`. Returns the final response, or null if the user cancelled (`submit` can also return
 *      null, if it has a confirmation of its own that the user cancelled - see `useRecalculationApproval`).
 *  - futureActionsDialog: the dialog element - must be rendered by the caller
 */
export function useFutureActionsConfirmation() {
    // Currently displayed dialog - { mode: "confirm" / "summary", actions, resolve }
    const [pending, setPending] = useState(null);

    const showDialog = (mode, actions) =>
        new Promise((resolve) => setPending({ mode, actions, resolve }));

    const closeDialog = (result) => {
        pending.resolve(result);
        setPending(null);
    };

    const submitWithConfirmation = async (submit) => {
        const response = await submit({});
        const futureActions = response?.data?.future_actions;
        if (!futureActions?.length) return response;

        // Nothing was saved - resolves to true (move to trash) / false (proceed without moving) / null (cancel)
        const trash = await showDialog("confirm", futureActions);
        if (trash === null) return null;

        const confirmedResponse = await submit({ trash_future_actions: trash });
        const trashedActions = confirmedResponse?.data?.trashed_actions;
        if (trashedActions?.length) {
            await showDialog("summary", trashedActions);
        }
        return confirmedResponse;
    };

    const isSummary = pending?.mode === "summary";

    const futureActionsDialog = (
        <Dialog
            open={pending !== null}
            // Closing the confirmation = cancel (the summary's result is ignored)
            onClose={() => closeDialog(null)}
            maxWidth="md"
            fullWidth={true}
        >
            {pending && (
                <div className="p-5 flex flex-col gap-6">
                    <div className="text-center text-3xl">
                        {isSummary
                            ? "Actions Moved to Trash"
                            : "Future Corporate/Index Actions"}
                    </div>
                    {isSummary ? (
                        <Alert severity="success">
                            {pending.actions.length}{" "}
                            {pending.actions.length === 1
                                ? "action was"
                                : "actions were"}{" "}
                            moved to trash (date set to 01/01/2500).
                        </Alert>
                    ) : (
                        <Alert severity="warning">
                            Nothing was saved yet - the security has the following actions dated after it
                            stops trading. Moving them to trash will set their execution/effective date to
                            01/01/2500.
                        </Alert>
                    )}
                    <FutureActionsTables actions={pending.actions} />
                    <div className="flex flex-row justify-center gap-4">
                        {isSummary ? (
                            <Button
                                variant="contained"
                                onClick={() => closeDialog(null)}
                            >
                                OK
                            </Button>
                        ) : (
                            <>
                                <Button
                                    variant="contained"
                                    color="warning"
                                    onClick={() => closeDialog(true)}
                                >
                                    Move to Trash
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => closeDialog(false)}
                                >
                                    Proceed without Moving
                                </Button>
                                <Button onClick={() => closeDialog(null)}>
                                    Cancel
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </Dialog>
    );

    return { submitWithConfirmation, futureActionsDialog };
}
